import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faFileInvoiceDollar,
    faChevronUp,
    faChevronDown,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import '../CRMProfileScreen.css';
import { getErrorMessage } from '../../utils/errorMessages';
import { getPartnerCommissionsOwed } from '../../services/statementService';

// IMT accent (green) — divergence from HMS (brand blue).
const IMT_ACCENT = '#059669';

// Semantic status colours (not branding): amber = pending, green = confirmed.
const COMMISSION_STAGE_COLORS = {
    pending: '#d97706',
    confirmed: '#067647',
};

const formatAmount = (value, currency) => {
    if (value === null || value === undefined || value === '') return '—';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(num)) return value;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num);
    } catch (_err) {
        return `${num.toFixed(2)} ${currency || ''}`.trim();
    }
};

const sectionCardStyle = {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '0.75rem',
    padding: '1.25rem',
};

const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 700,
    fontSize: '1rem',
    color: '#111827',
    marginBottom: '1rem',
};

// Per-currency pending/confirmed subtotal card. The commissions payload is
// keyed as pending/confirmed/total strings per currency, so this reads the
// requested stageKey directly rather than the AR/AP by_currency shape.
const CommissionTotalsRow = ({ side, byCurrency, stageKey, color }) => {
    const currencies = (byCurrency || []).filter(
        (row) => parseFloat(row[stageKey]) > 0,
    );
    if (currencies.length === 0) {
        return (
            <div
                style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    color: '#6b7280',
                    fontSize: '0.9rem',
                    flex: '1 1 220px',
                    minWidth: '220px',
                }}
            >
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.25rem' }}>
                    {side}
                </div>
                None.
            </div>
        );
    }

    return (
        <div
            style={{
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                padding: '1rem',
                flex: '1 1 220px',
                minWidth: '220px',
                borderTop: `4px solid ${color}`,
            }}
        >
            <div
                style={{
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                }}
            >
                {side}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {currencies.map((row) => (
                    <div
                        key={row.currency}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            gap: '0.5rem',
                            paddingBottom: '0.4rem',
                            borderBottom: '1px dashed #f3f4f6',
                        }}
                    >
                        <div>
                            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>
                                {formatAmount(row[stageKey], row.currency)}
                            </div>
                            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                                {row.currency}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const CommissionStageBadge = ({ status }) => {
    const color = COMMISSION_STAGE_COLORS[status] || '#6b7280';
    return (
        <span
            style={{
                background: `${color}1A`,
                color,
                padding: '0.15rem 0.5rem',
                borderRadius: '999px',
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.03em',
                whiteSpace: 'nowrap',
            }}
        >
            {status || '—'}
        </span>
    );
};

const CommissionLinesTable = ({ lines }) => (
    <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
                <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #e5e7eb' }}>
                        Deal
                    </th>
                    <th style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #e5e7eb' }}>
                        Side
                    </th>
                    <th style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #e5e7eb' }}>
                        Stage
                    </th>
                    <th style={{ padding: '0.5rem 0.6rem', borderBottom: '1px solid #e5e7eb' }}>
                        Currency
                    </th>
                    <th
                        style={{
                            padding: '0.5rem 0.6rem',
                            borderBottom: '1px solid #e5e7eb',
                            textAlign: 'right',
                        }}
                    >
                        Amount
                    </th>
                </tr>
            </thead>
            <tbody>
                {lines.map((line) => (
                    <tr key={line.id} style={{ borderBottom: '1px solid #f3f4f6', background: 'white' }}>
                        <td style={{ padding: '0.5rem 0.6rem', fontWeight: 600, color: '#111827' }}>
                            {line.deal_ref || '—'}
                        </td>
                        <td style={{ padding: '0.5rem 0.6rem', color: '#4b5563' }}>
                            {line.side === 'sell' ? 'Sell' : line.side === 'buy' ? 'Buy' : '—'}
                        </td>
                        <td style={{ padding: '0.5rem 0.6rem' }}>
                            <CommissionStageBadge status={line.status} />
                        </td>
                        <td style={{ padding: '0.5rem 0.6rem', color: '#4b5563' }}>
                            {line.currency || '—'}
                        </td>
                        <td
                            style={{
                                padding: '0.5rem 0.6rem',
                                textAlign: 'right',
                                fontWeight: 600,
                            }}
                        >
                            {formatAmount(line.amount, line.currency)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const CRMFinancialAccountingTab = ({ profileData, isNewProfile }) => {
    const partnerId = profileData?.id;

    const [commissionsData, setCommissionsData] = useState(null);
    const [commissionsLoading, setCommissionsLoading] = useState(false);
    const [commissionsError, setCommissionsError] = useState(null);
    const [showCommissionLines, setShowCommissionLines] = useState(false);

    const fetchCommissions = useCallback(async () => {
        if (!partnerId) return;
        setCommissionsLoading(true);
        setCommissionsError(null);
        try {
            const payload = await getPartnerCommissionsOwed(partnerId);
            setCommissionsData(payload);
        } catch (err) {
            console.error('Financial Tab: failed to load commissions owed', err);
            setCommissionsError(getErrorMessage(err, 'Failed to load commissions owed.'));
        } finally {
            setCommissionsLoading(false);
        }
    }, [partnerId]);

    useEffect(() => {
        if (partnerId && !isNewProfile) {
            fetchCommissions();
        }
    }, [partnerId, isNewProfile, fetchCommissions]);

    if (isNewProfile || !partnerId) {
        return (
            <div className="crm-tab-panel">
                <div className="crm-form-section">
                    <div className="crm-placeholder-container">
                        <div className="crm-placeholder-header">
                            <div className="crm-placeholder-icon">
                                <span style={{ fontSize: '3rem' }}>$</span>
                            </div>
                            <h2 className="crm-placeholder-title">Financial &amp; Accounting</h2>
                            <p className="crm-placeholder-subtitle">
                                Save the partner to view commissions owed.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="crm-tab-panel">
            <div className="crm-form-section">
                <div style={sectionCardStyle}>
                    <div style={sectionHeaderStyle}>
                        <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: IMT_ACCENT }} />
                        Commissions Owed
                    </div>

                    {commissionsError && (
                        <div
                            style={{
                                background: '#fef2f2',
                                border: '1px solid #ef4444',
                                color: '#dc2626',
                                borderRadius: '0.5rem',
                                padding: '0.75rem 1rem',
                                marginBottom: '1rem',
                                display: 'flex',
                                gap: '0.5rem',
                                alignItems: 'flex-start',
                            }}
                        >
                            <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginTop: '0.15rem' }} />
                            <span>{commissionsError}</span>
                        </div>
                    )}

                    {commissionsLoading ? (
                        <div
                            style={{
                                padding: '2rem',
                                textAlign: 'center',
                                color: '#6b7280',
                                background: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                borderRadius: '0.5rem',
                            }}
                        >
                            Loading commissions…
                        </div>
                    ) : commissionsData && commissionsData.count === 0 ? (
                        <div
                            style={{
                                padding: '1.5rem',
                                textAlign: 'center',
                                color: '#6b7280',
                                background: '#f9fafb',
                                border: '1px dashed #e5e7eb',
                                borderRadius: '0.5rem',
                            }}
                        >
                            No commissions owed for this partner.
                        </div>
                    ) : commissionsData ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '1rem',
                                    marginBottom: '1rem',
                                }}
                            >
                                <CommissionTotalsRow
                                    side="Pending"
                                    byCurrency={commissionsData.by_currency}
                                    stageKey="pending"
                                    color={COMMISSION_STAGE_COLORS.pending}
                                />
                                <CommissionTotalsRow
                                    side="Confirmed"
                                    byCurrency={commissionsData.by_currency}
                                    stageKey="confirmed"
                                    color={COMMISSION_STAGE_COLORS.confirmed}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowCommissionLines((v) => !v)}
                                style={{
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.5rem',
                                    padding: '0.6rem 1rem',
                                    color: '#374151',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    width: '100%',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <span>
                                    <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '0.5rem', color: IMT_ACCENT }} />
                                    Commission line items ({commissionsData.count})
                                </span>
                                <FontAwesomeIcon icon={showCommissionLines ? faChevronUp : faChevronDown} />
                            </button>

                            {showCommissionLines && (
                                <div style={{ marginTop: '1rem' }}>
                                    <CommissionLinesTable lines={commissionsData.lines || []} />
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default CRMFinancialAccountingTab;
